import { ApiError, NetworkError, type ApiRequestOptions } from './types';
import type { SessionToken } from '../session';

interface ClientConfig {
  baseUrl: string;
  getToken: () => SessionToken | null;
}

export interface ApiClient {
  request<T = unknown>(path: string, opts?: ApiRequestOptions): Promise<T>;
}

export function createApiClient({ baseUrl, getToken }: ClientConfig): ApiClient {
  async function doFetch(path: string, opts: ApiRequestOptions): Promise<Response> {
    const url = baseUrl + path;
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const token = getToken();
    if (token) headers.set('X-Platform-Session', token);

    const init: RequestInit = {
      method: opts.method ?? 'GET',
      headers,
    };
    if (opts.signal) init.signal = opts.signal;
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

    try {
      return await fetch(url, init);
    } catch (err) {
      throw new NetworkError(err instanceof Error ? err.message : 'unknown');
    }
  }

  async function parseError(res: Response): Promise<ApiError> {
    let code: string | undefined;
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { code?: string; message?: string };
      code = body.code;
      if (body.message) message = body.message;
    } catch {
      // non-JSON body
    }
    return new ApiError(res.status, code, message);
  }

  return {
    async request<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
      let res = await doFetch(path, opts);
      if (res.status >= 500) {
        await new Promise((r) => setTimeout(r, 300));
        res = await doFetch(path, opts);
      }
      if (!res.ok) throw await parseError(res);
      const contentType = res.headers.get('content-type') ?? '';
      // Match application/json AND application/vnd.api+json, ld+json, etc.
      if (/\bjson\b/i.test(contentType)) return (await res.json()) as T;
      return undefined as T;
    },
  };
}
